<template>
  <v-dialog
    v-model="show"
    width="900"
    scrollable
    persistent
  >
    <v-card :loading="loadingCrons || runningCommand || loadingArgOpt">
      <v-card-title>
        <span class="headline">Run CronJob Now</span>
      </v-card-title>

      <v-card-text>
        <v-container
          v-if="loadingCrons"
          fluid
          fill-height
        >
          <v-row
            align="center"
            justify="center"
          >
            <v-progress-circular
              :size="50"
              color="primary"
              indeterminate
            />
          </v-row>
        </v-container>
        <v-container
          v-else
        >
          <v-form>
            <v-row>
              <v-col
                cols="12"
                md="12"
              >
                <v-autocomplete
                  v-model="commandSelected"
                  :items="commands"
                  :search-input.sync="search"
                  :loading="isLoading"
                  label="Type your command"
                  item-text="text"
                  item-value="key"
                  clearable
                  cache-items
                >
                  <template #selection="data">
                    <v-chip>
                      {{ data.item.text }}
                    </v-chip>
                  </template>
                  <template #item="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.text }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <p v-if="commandSelected && currentCommand">
                  Command Description: {{ currentCommand.description }}
                </p>
              </v-col>
            </v-row>
            <v-row
              v-if="commandSelected"
            >
              <v-col
                cols="6"
                md="6"
                sm="6"
              >
                <CronjobArguments
                  v-model="cronArguments"
                  :arguments-array="argumentsArray"
                />
              </v-col>
              <v-col
                cols="6"
                md="6"
                sm="6"
              >
                <CronjobOptions
                  v-model="cronOptions"
                  :options-array="optionsArray"
                />
              </v-col>
            </v-row>
          </v-form>
          <v-row
            v-if="commandSelected"
          >
            <v-col
              cols="12"
              sm="12"
              md="12"
            >
              <div class="console-simulator">
                php artisan{{ commandPrebuild }}
                <br>

                <div v-if="commandRan">
                  <span>
                    Exit Code: {{ commandExitCode }}
                  </span>

                  <p>
                    Command Result: {{ commandResult }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="12"
            >
              <v-btn
                color="primary"
                dark
                small
                :disabled="!ableToRun || runningCommand"
                :loading="runningCommand"
                @click="runCommand()"
              >
                Run
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="blue darken-1"
          text
          :disabled="runningCommand"
          @click="close()"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import CronjobArguments from '@/views/Settings/Cronjob/RunCronjobNow/CronjobArguments.vue';
import CronjobOptions from '@/views/Settings/Cronjob/RunCronjobNow/CronjobOptions.vue';

export default {
  components: { CronjobOptions, CronjobArguments },
  validations: {
    commandSelected: {
      required,
    },
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      commands: [],
      commandSelected: null,
      loadingCrons: false,
      loadingArgOpt: false,
      argumentsArray: [],
      optionsArray: [],
      cronArguments: {},
      cronOptions: {},

      runningCommand: false,
      commandResult: '',
      commandExitCode: 1,
      commandRan: false,

      selectedOption: null,
      options: [],
      search: null,
      isLoading: false,
    };
  },
  computed: {
    show: {
      get() {
        if (this.value) {
          this.loadCronJobsCommands();
        }
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    ableToRun() {
      return this.commandSelected;
    },

    currentCommand() {
      return this.commands.find((comm) => comm.key === this.commandSelected);
    },

    commandPrebuild() {
      let command = '';
      if (this.commandSelected) {
        command = `${command} ${this.commandSelected}`;
      }

      if (Object.keys(this.cronArguments).length) {
        const cronArgumentsArray = [];

        Object.keys(this.cronArguments).forEach((key) => {
          cronArgumentsArray.push({ key, value: this.cronArguments[key] });
        });

        cronArgumentsArray.sort((a, b) => a.value.order - b.value.order);

        cronArgumentsArray.forEach((cron) => {
          const argumentInOrder = cron.value.value;
          if (argumentInOrder) {
            command = `${command} ${argumentInOrder}`;
          }
        });
      }

      if (Object.keys(this.cronOptions).length) {
        Object.keys(this.cronOptions).forEach((key) => {
          if (this.cronOptions[key].value || this.cronOptions[key].justOption) {
            if (this.cronOptions[key].value) {
              command = `${command} --${key}=${this.cronOptions[key].value}`;
            } else {
              command = `${command} --${key}`;
            }
          }
        });
      }

      return command;
    },
  },

  watch: {
    commandSelected(newValue) {
      if (newValue) {
        this.loadArgumentsAndOptions();
      }
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    loadCronJobsCommands() {
      this.loadingCrons = true;
      this.$axios.get('/api/v1/cronjobs/all')
        .then((response) => {
          const { data } = response;

          this.commands = data.data ?? [];
        })
        .catch(() => {
          this.setNotification({
            message: 'There was an error processing your request.',
            color: 'red',
          });
        })
        .finally(() => {
          this.loadingCrons = false;
        });
    },

    loadArgumentsAndOptions() {
      this.loadingArgOpt = true;
      this.commandResult = '';
      this.commandExitCode = 1;
      this.commandRan = false;
      this.argumentsArray = [];
      this.optionsArray = [];
      this.cronArguments = {};
      this.cronOptions = {};

      this.$axios.post('/api/v1/cronjobs/argument-option', {
        commandSelected: this.getCommandSelected(),
      })
        .then((response) => {
          const { data } = response;
          this.argumentsArray = data.data?.arguments ?? [];
          this.optionsArray = data.data?.options ?? [];
        })
        .catch(() => {
          this.setNotification({
            message: 'There was an error processing your request.',
            color: 'red',
          });
        })
        .finally(() => {
          this.loadingArgOpt = false;
        });
    },

    getCommandSelected() {
      return this.commands.find((command) => command.key === this.commandSelected);
    },

    close() {
      this.$v.$reset();

      this.$emit('close-modal');

      this.clean();
    },

    clean() {
      this.commands = [];
      this.commandSelected = null;
      this.loadingCrons = false;
      this.loadingArgOpt = false;
      this.argumentsArray = [];
      this.optionsArray = [];
      this.cronArguments = {};
      this.cronOptions = {};

      this.runningCommand = false;
      this.commandResult = '';
      this.commandExitCode = 1;
      this.commandRan = false;
    },

    runCommand() {
      this.runningCommand = true;
      this.commandResult = '';
      this.commandExitCode = 1;
      this.commandRan = false;

      this.$axios.post('/api/v1/cronjobs/run-command', {
        command: this.commandPrebuild,
      })
        .then((response) => {
          const { data } = response;

          this.commandRan = true;
          this.commandExitCode = data.data?.exist_code ?? 1;

          this.commandResult = data.data?.output ?? '';

          this.setNotification({
            color: 'green',
            message: data.message ?? 'Command executed successfully!',
          });
        })
        .catch((error) => {
          const message = 'There was an error processing your request.';

          this.setNotification({
            message: error.response?.data?.message ?? message,
            color: 'red',
          });
        })
        .finally(() => {
          this.runningCommand = false;
        });
    },
  },
};
</script>

<style scoped>
.console-simulator {
  min-height: 350px;
  overflow: auto;
  border-radius: 4px;
  background-color: #000000;
  color: #00ff00;
  padding: 10px;
  font-family: "Courier New", Courier, monospace;
  white-space: pre;
  width: 100%;
  height: 100%;
}
</style>
